import React, { useState, useEffect } from "react";
import { MapContainer, ImageOverlay, useMap, useMapEvents, Polyline, Polygon, Circle, CircleMarker, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MAP_CONFIG, STATIC_CONTINENTS } from "./mapData";
import L from "leaflet";
import CityNotesPanel from "./CityNotesPanel";

const TRAVEL_MODES = [
    { id: "foot_fast", name: "On Foot (Fast)", speed: 4, hoursPerDay: 8, category: "On Foot" },
    { id: "foot_normal", name: "On Foot (Normal)", speed: 3, hoursPerDay: 8, category: "On Foot" },
    { id: "foot_slow", name: "On Foot (Slow)", speed: 2, hoursPerDay: 8, category: "On Foot" },
    { id: "horse", name: "Horseback", speed: 6, hoursPerDay: 8, category: "Mount" },
    { id: "cart", name: "Cart", speed: 2, hoursPerDay: 8, category: "Vehicle" },
    { id: "galley", name: "Galley", speed: 4, hoursPerDay: 24, category: "Ship" },
    { id: "train", name: "Train", speed: 30, hoursPerDay: 24, category: "Vehicle" },
    { id: "airship_cruiser", name: "Airship Cruiser", speed: 10, hoursPerDay: 24, category: "Airship" },
    { id: "airship_strider", name: "Airship Strider", speed: 8, hoursPerDay: 24, category: "Airship" },
    { id: "airship_skykiff", name: "Airship Skykiff", speed: 7, hoursPerDay: 24, category: "Airship" },
];



function InitialView({ bounds }) {
    const map = useMap();
    useEffect(() => {
        map.fitBounds(bounds);
    }, []); // Only run once on mount
    return null;
}

// Color mapping for travel mode categories
const MODE_COLORS = {
    "On Foot": "#ff6b6b",
    "Mount": "#f39c12",
    "Vehicle": "#9b59b6",
    "Ship": "#3498db",
    "Airship": "#2ecc71"
};

// Helper: calculate distance between two points
const calculateSegmentDistance = (from, to) => {
    const dx = to[1] - from[1];
    const dy = to[0] - from[0];
    return Math.sqrt(dx * dx + dy * dy) / MAP_CONFIG.pixelsPerMile;
};

// Helper: group consecutive segments by mode and calculate proper travel time
const calculateRouteStats = (segments) => {
    if (segments.length < 2) return { totalDistance: 0, totalHours: 0, groups: [], legs: [] };

    // Build list of legs with distances
    const legs = [];
    for (let i = 1; i < segments.length; i++) {
        legs.push({
            from: segments[i - 1].point,
            to: segments[i].point,
            distance: calculateSegmentDistance(segments[i - 1].point, segments[i].point),
            mode: segments[i].mode
        });
    }

    // Group consecutive legs by mode
    const groups = [];
    let currentGroup = null;

    for (const leg of legs) {
        if (currentGroup && currentGroup.mode.id === leg.mode.id) {
            // Same mode - add to current group
            currentGroup.distance += leg.distance;
            currentGroup.legCount++;
        } else {
            // Different mode - start new group
            if (currentGroup) groups.push(currentGroup);
            currentGroup = {
                mode: leg.mode,
                distance: leg.distance,
                legCount: 1
            };
        }
    }
    if (currentGroup) groups.push(currentGroup);

    // Calculate time for each group (applying daily limits to grouped distance)
    let totalDistance = 0;
    let totalHours = 0;

    for (const group of groups) {
        group.hours = group.distance / group.mode.speed; // actual travel hours
        group.days = group.hours / group.mode.hoursPerDay; // travel days for this mode
        totalDistance += group.distance;
        totalHours += group.hours;
    }

    return { totalDistance, totalHours, groups, legs };
};

// Helper: format travel time for a specific group using its mode's hoursPerDay
const formatGroupTime = (hours, hoursPerDay) => {
    const days = Math.floor(hours / hoursPerDay);
    const remainingHours = hours - (days * hoursPerDay);
    const h = Math.floor(remainingHours);
    const m = Math.round((remainingHours - h) * 60);
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0 && days === 0) parts.push(`${m}m`);
    return parts.length > 0 ? parts.join(" ") : "< 1m";
};

// Helper: format total travel time from groups
const formatTotalTime = (groups) => {
    if (groups.length === 0) return "< 1m";

    // If single mode, use that mode's format
    if (groups.length === 1) {
        return formatGroupTime(groups[0].hours, groups[0].mode.hoursPerDay);
    }

    // For mixed modes, sum the displayed days and hours from each group
    let totalDays = 0;
    let totalRemainingHours = 0;

    for (const group of groups) {
        const days = Math.floor(group.hours / group.mode.hoursPerDay);
        const remainingHours = group.hours - (days * group.mode.hoursPerDay);
        totalDays += days;
        totalRemainingHours += remainingHours;
    }

    const h = Math.floor(totalRemainingHours);
    const m = Math.round((totalRemainingHours - h) * 60);

    const parts = [];
    if (totalDays > 0) parts.push(`${totalDays}d`);
    if (h > 0) parts.push(`${h}h`);
    if (m > 0 && totalDays === 0) parts.push(`${m}m`);
    return parts.length > 0 ? parts.join(" ") : "< 1m";
};

function MeasurementTool({ segments, setSegments, measuring, travelMode }) {
    useMapEvents({
        click(e) {
            if (!measuring) return;
            const newPoint = [e.latlng.lat, e.latlng.lng];
            if (segments.length === 0) {
                // First point - no travel mode yet
                setSegments([{ point: newPoint, mode: null }]);
            } else {
                // Subsequent points use current travel mode
                setSegments(prev => [...prev, { point: newPoint, mode: travelMode }]);
            }
        }
    });

    if (segments.length === 0) return null;

    const { totalDistance, groups } = calculateRouteStats(segments);

    return (
        <>
            {/* Draw segments with different colors based on mode */}
            {segments.length > 1 && segments.slice(1).map((segment, index) => (
                <Polyline
                    key={index}
                    positions={[segments[index].point, segment.point]}
                    color={MODE_COLORS[segment.mode.category]}
                    weight={4}
                    dashArray="8, 8"
                />
            ))}
            {/* Draw points */}
            {segments.map((segment, index) => (
                <CircleMarker
                    key={index}
                    center={segment.point}
                    radius={index === 0 ? 8 : 6}
                    fillColor={index === 0 ? "#fff" : MODE_COLORS[segment.mode?.category] || "#fff"}
                    fillOpacity={1}
                    color={index === 0 ? "#2ecc71" : "#fff"}
                    weight={2}
                >
                    {/* Show total on last point */}
                    {index === segments.length - 1 && segments.length > 1 && (
                        <Tooltip permanent direction="top" offset={[0, -10]}>
                            <div style={{ textAlign: "center" }}>
                                <div style={{ fontWeight: "bold" }}>{totalDistance.toFixed(1)} miles</div>
                                <div style={{ fontSize: "0.9em", opacity: 0.9 }}>{formatTotalTime(groups)}</div>
                            </div>
                        </Tooltip>
                    )}
                </CircleMarker>
            ))}
        </>
    );
}

function RouteSummary({ segments }) {
    if (segments.length < 2) return null;

    const { totalDistance, groups } = calculateRouteStats(segments);

    return (
        <div style={{
            backgroundColor: "rgba(74, 85, 104, 0.95)",
            padding: "10px 12px",
            borderRadius: "4px",
            maxHeight: "200px",
            overflowY: "auto",
            minWidth: "220px"
        }}>
            <div style={{ color: "white", fontWeight: "bold", marginBottom: "8px", borderBottom: "1px solid rgba(255,255,255,0.2)", paddingBottom: "6px" }}>
                Route Summary
            </div>
            {groups.map((group, idx) => (
                <div key={idx} style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "4px 0",
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.9)"
                }}>
                    <div style={{
                        width: "8px",
                        height: "8px",
                        backgroundColor: MODE_COLORS[group.mode.category],
                        borderRadius: "2px",
                        flexShrink: 0
                    }} />
                    <span style={{ flex: 1 }} title={group.mode.name}>
                        {group.mode.name}
                        {group.legCount > 1 && <span style={{ opacity: 0.6 }}> ({group.legCount} legs)</span>}
                    </span>
                    <span style={{ opacity: 0.8 }}>{group.distance.toFixed(1)} mi</span>
                    <span style={{ fontWeight: "bold" }}>{formatGroupTime(group.hours, group.mode.hoursPerDay)}</span>
                </div>
            ))}
            <div style={{
                borderTop: "1px solid rgba(255,255,255,0.2)",
                marginTop: "8px",
                paddingTop: "8px",
                display: "flex",
                justifyContent: "space-between",
                color: "white",
                fontWeight: "bold"
            }}>
                <span>Total</span>
                <span>{totalDistance.toFixed(1)} mi / {formatTotalTime(groups)}</span>
            </div>
        </div>
    );
}

function ContinentOverlays({ continents }) {
    return (
        <>
            {continents.map((continent) => (
                <Polygon
                    key={continent.id}
                    positions={continent.points}
                    pathOptions={{
                        color: continent.color,
                        fillColor: continent.color,
                        fillOpacity: 0.2,
                        weight: 2
                    }}
                >
                    <Tooltip sticky>
                        {continent.name}
                    </Tooltip>
                </Polygon>
            ))}
        </>
    );
}

// City marker sizes in map units (pixels on the map image)
const CITY_SIZES = {
    metropolis: 30,
    city: 20,
    town: 12
};

const CITY_INFO = {
    metropolis: { label: "Metropolis", population: "20k - 500k", color: "#ffd700" },
    city: { label: "City", population: "6k - 20k", color: "#fff" },
    town: { label: "Town", population: "1k - 6k", color: "#fff" }
};

const CITY_FONT_SIZES = {
    metropolis: 6,
    city: 4,
    town: 2
};

// Create diamond polygon points around a center position
const createDiamondPoints = (center, size) => {
    const [y, x] = center;
    return [
        [y - size, x],         // top
        [y, x + size],         // right
        [y + size, x],         // bottom
        [y, x - size]          // left
    ];
};

function CityMarkers({ cities, onEditCity, measuring }) {
    const map = useMap();
    const [zoom, setZoom] = useState(map.getZoom());

    // Set initial CSS variable for label scale
    useEffect(() => {
        map.getContainer().style.setProperty('--city-scale', Math.pow(2, map.getZoom() + 2.5));
    }, [map]);

    useMapEvents({
        zoomanim(e) {
            // Update CSS variable directly — no React re-render, so markers aren't disturbed
            map.getContainer().style.setProperty('--city-scale', Math.pow(2, e.zoom + 2.5));
        },
        zoomend() {
            const z = map.getZoom();
            setZoom(z);
            map.getContainer().style.setProperty('--city-scale', Math.pow(2, z + 2.5));
        }
    });

    if (cities.length === 0) return null;

    return (
        <>
            {cities.map((city) => {
                const size = CITY_SIZES[city.size] || CITY_SIZES.town;
                const travelIcons = [];
                if (city.hasTrainStation) travelIcons.push("🚂");
                if (city.hasPort) travelIcons.push("⚓");
                if (city.hasSkyport) travelIcons.push("🎈");

                const markerPixels = size * Math.pow(2, zoom);
                const nameOffset = [0, 0]; // [0, -markerPixels * 1.2];
                const fontSize = CITY_FONT_SIZES[city.size] || 4;

                const nameLabel = (
                    <Tooltip permanent direction="top" offset={nameOffset} className="city-label">
                        <div className="city-label-content" style={{
                            transformOrigin: "bottom center",
                            whiteSpace: "nowrap",
                            fontWeight: "bold",
                            fontSize: `${fontSize}px`,
                        }}>
                            {city.name}
                        </div>
                    </Tooltip>
                );

                const handleClick = () => { if (onEditCity && !measuring) onEditCity(city); };

                const hoverInfo = travelIcons.length > 0 ? (
                    <CircleMarker
                        key={city.id + "_hover"}
                        center={city.position}
                        radius={Math.max(markerPixels, 6)}
                        pathOptions={{ opacity: 0, fillOpacity: 0 }}
                        eventHandlers={{ click: handleClick }}
                    >
                        <Tooltip direction="top" className="city-hover">
                            <div style={{ textAlign: "center" }}>
                                <strong>{city.name}</strong>
                                <div>{travelIcons.join(" ")}</div>
                            </div>
                        </Tooltip>
                    </CircleMarker>
                ) : null;

                // Town uses circle, city and metropolis use diamond
                if (city.size === "town") {
                    return (
                        <React.Fragment key={city.id}>
                            <Circle
                                center={city.position}
                                radius={size}
                                pathOptions={{
                                    fillColor: "#fff",
                                    fillOpacity: 1,
                                    color: "#666",
                                    weight: 2
                                }}
                                eventHandlers={{ click: handleClick }}
                            >
                                {nameLabel}
                            </Circle>
                            {hoverInfo}
                        </React.Fragment>
                    );
                } else {
                    // Diamond for city and metropolis
                    const isMetropolis = city.size === "metropolis";
                    return (
                        <React.Fragment key={city.id}>
                            <Polygon
                                positions={createDiamondPoints(city.position, size)}
                                pathOptions={{
                                    fillColor: isMetropolis ? "#ffd700" : "#fff",
                                    fillOpacity: 1,
                                    color: isMetropolis ? "#8b4513" : "#333",
                                    weight: isMetropolis ? 3 : 2
                                }}
                                eventHandlers={{ click: handleClick }}
                            >
                                {nameLabel}
                            </Polygon>
                            {hoverInfo}
                        </React.Fragment>
                    );
                }
            })}
        </>
    );
}

function CoordinatePicker({ enabled, onCoordinateClick }) {
    useMapEvents({
        click(e) {
            if (!enabled) return;
            const coords = [Math.round(e.latlng.lat), Math.round(e.latlng.lng)];
            onCoordinateClick(coords);
        }
    });
    return null;
}

function CityPlacer({ active, onPlace }) {
    useMapEvents({
        click(e) {
            if (!active) return;
            onPlace([Math.round(e.latlng.lat), Math.round(e.latlng.lng)]);
        }
    });
    return null;
}

function ToggleSwitch({ enabled, onToggle, label }) {
    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            backgroundColor: "rgba(74, 85, 104, 0.9)",
            padding: "8px 12px",
            borderRadius: "4px"
        }}>
            <span style={{ color: "white", fontSize: "14px" }}>{label}</span>
            <div
                onClick={onToggle}
                style={{
                    width: "44px",
                    height: "24px",
                    backgroundColor: enabled ? "#2ecc71" : "#4a5568",
                    borderRadius: "12px",
                    cursor: "pointer",
                    position: "relative",
                    transition: "background-color 0.2s"
                }}
            >
                <div style={{
                    width: "20px",
                    height: "20px",
                    backgroundColor: "white",
                    borderRadius: "50%",
                    position: "absolute",
                    top: "2px",
                    left: enabled ? "22px" : "2px",
                    transition: "left 0.2s"
                }} />
            </div>
        </div>
    );
}

export default function LeafletMap() {
    const [measuring, setMeasuring] = useState(false);
    const [segments, setSegments] = useState([]);
    const [politicalOpacity, setPoliticalOpacity] = useState(0);
    const [showContinents, setShowContinents] = useState(false);
    const [showCities, setShowCities] = useState(false);
    const [travelMode, setTravelMode] = useState(TRAVEL_MODES[1]); // Default: On Foot (Normal)
    const [cities, setCities] = useState([]);
    const [pickingCoords, setPickingCoords] = useState(false);
    const [lastCoords, setLastCoords] = useState(null);
    const [addingCity, setAddingCity] = useState(false);
    const [newCityPos, setNewCityPos] = useState(null);
    const [cityForm, setCityForm] = useState({ name: "", size: "town", hasTrainStation: false, hasPort: false, hasSkyport: false });
    const [cityError, setCityError] = useState(null);
    const [citySaving, setCitySaving] = useState(false);
    const [editingCity, setEditingCity] = useState(null);
    const [editForm, setEditForm] = useState({ name: "", size: "town", hasTrainStation: false, hasPort: false, hasSkyport: false });
    const [notesCity, setNotesCity] = useState(null);

    useEffect(() => {
        fetch("/api/cities.php")
            .then(res => res.json())
            .then(data => setCities(data))
            .catch(() => setCities([]));
    }, []);

    const bounds = [[0, 0], [MAP_CONFIG.height, MAP_CONFIG.width]];

    const toggleMeasuring = () => {
        if (measuring) {
            setSegments([]);
        }
        setMeasuring(!measuring);
    };

    const toggleAddingCity = () => {
        if (addingCity) {
            setNewCityPos(null);
            setCityForm({ name: "", size: "town", hasTrainStation: false, hasPort: false, hasSkyport: false });
            setCityError(null);
        }
        setAddingCity(!addingCity);
    };

    const saveCity = async () => {
        setCityError(null);
        setCitySaving(true);
        try {
            const res = await fetch("/api/cities.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...cityForm, position: newCityPos }),
            });
            const data = await res.json();
            if (!res.ok) {
                setCityError(data.error || "Failed to save city");
                return;
            }
            await fetchCities();
            setShowCities(true);
            // Reset
            setAddingCity(false);
            setNewCityPos(null);
            setCityForm({ name: "", size: "town", hasTrainStation: false, hasPort: false, hasSkyport: false });
        } catch {
            setCityError("Network error");
        } finally {
            setCitySaving(false);
        }
    };

    const fetchCities = async () => {
        const res = await fetch("/api/cities.php");
        const data = await res.json();
        setCities(data);
    };

    const onCityClick = (city) => {
        setNotesCity(city);
        setEditingCity(null);
        setCityError(null);
    };

    const startEditCity = (city) => {
        setEditingCity(city);
        setEditForm({
            name: city.name,
            size: city.size,
            hasTrainStation: city.hasTrainStation,
            hasPort: city.hasPort,
            hasSkyport: city.hasSkyport,
        });
        setCityError(null);
    };

    const cancelEdit = () => {
        setEditingCity(null);
        setCityError(null);
    };

    const updateCity = async () => {
        setCityError(null);
        setCitySaving(true);
        try {
            const res = await fetch("/api/cities.php", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    id: editingCity.id,
                    name: editForm.name,
                    position: editingCity.position,
                    size: editForm.size,
                    hasTrainStation: editForm.hasTrainStation,
                    hasPort: editForm.hasPort,
                    hasSkyport: editForm.hasSkyport,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                setCityError(data.error || "Failed to update city");
                return;
            }
            await fetchCities();
            setEditingCity(null);
        } catch {
            setCityError("Network error");
        } finally {
            setCitySaving(false);
        }
    };

    const deleteCity = async () => {
        if (!window.confirm(`Delete "${editingCity.name}"? This cannot be undone.`)) return;
        setCityError(null);
        setCitySaving(true);
        try {
            const res = await fetch("/api/cities.php", {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: editingCity.id }),
            });
            const data = await res.json();
            if (!res.ok) {
                setCityError(data.error || "Failed to delete city");
                return;
            }
            await fetchCities();
            setEditingCity(null);
        } catch {
            setCityError("Network error");
        } finally {
            setCitySaving(false);
        }
    };

    const clearMeasurement = () => {
        setSegments([]);
    };

    const undoLastPoint = () => {
        setSegments(prev => prev.slice(0, -1));
    };

    return (
        <div style={{ height: "100%", width: "100%" }}>
            <div style={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                alignItems: "flex-end"
            }}>
                <div style={{ display: "flex", gap: "8px" }}>
                    <a
                        href="/notes/"
                        style={{
                            padding: "8px 16px",
                            backgroundColor: "#ffd700",
                            color: "#1a202c",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontWeight: "bold",
                            textDecoration: "none"
                        }}
                    >
                        Campaign Notes
                    </a>
                    <button
                        onClick={toggleMeasuring}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: measuring ? "#ff6b6b" : "#4a5568",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontWeight: "bold"
                        }}
                    >
                        {measuring ? "Stop Measuring" : "Measure Distance"}
                    </button>
                    <button
                        onClick={() => {
                            setPickingCoords(!pickingCoords);
                            if (!pickingCoords) setLastCoords(null);
                        }}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: pickingCoords ? "#3498db" : "#4a5568",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontWeight: "bold"
                        }}
                    >
                        {pickingCoords ? "Stop Picking" : "Pick Coords"}
                    </button>
                    <button
                        onClick={toggleAddingCity}
                        style={{
                            padding: "8px 16px",
                            backgroundColor: addingCity ? "#ff6b6b" : "#2ecc71",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontWeight: "bold"
                        }}
                    >
                        {addingCity ? "Cancel" : "Add City"}
                    </button>
                    {segments.length > 0 && (
                        <>
                            <button
                                onClick={undoLastPoint}
                                style={{
                                    padding: "8px 16px",
                                    backgroundColor: "#f39c12",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer"
                                }}
                            >
                                Undo
                            </button>
                            <button
                                onClick={clearMeasurement}
                                style={{
                                    padding: "8px 16px",
                                    backgroundColor: "#4a5568",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer"
                                }}
                            >
                                Clear
                            </button>
                        </>
                    )}
                </div>
                {pickingCoords && (
                    <div style={{
                        backgroundColor: "rgba(52, 152, 219, 0.95)",
                        padding: "10px 12px",
                        borderRadius: "4px",
                        minWidth: "200px"
                    }}>
                        <div style={{ color: "white", fontSize: "12px", marginBottom: "6px" }}>
                            Click on the map to get coordinates
                        </div>
                        {lastCoords && (
                            <div style={{
                                backgroundColor: "rgba(0,0,0,0.3)",
                                padding: "8px",
                                borderRadius: "4px",
                                fontFamily: "monospace"
                            }}>
                                <div style={{ color: "white", fontSize: "14px" }}>
                                    [{lastCoords[0]}, {lastCoords[1]}]
                                </div>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(`[${lastCoords[0]}, ${lastCoords[1]}]`);
                                    }}
                                    style={{
                                        marginTop: "6px",
                                        padding: "4px 8px",
                                        backgroundColor: "#2980b9",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "3px",
                                        cursor: "pointer",
                                        fontSize: "12px"
                                    }}
                                >
                                    Copy to Clipboard
                                </button>
                            </div>
                        )}
                    </div>
                )}
                {addingCity && !newCityPos && (
                    <div style={{
                        backgroundColor: "rgba(46, 204, 113, 0.95)",
                        padding: "10px 12px",
                        borderRadius: "4px",
                        minWidth: "200px"
                    }}>
                        <div style={{ color: "white", fontSize: "12px" }}>
                            Click on the map to place a city
                        </div>
                    </div>
                )}
                {addingCity && newCityPos && (
                    <div style={{
                        backgroundColor: "rgba(74, 85, 104, 0.95)",
                        padding: "10px 12px",
                        borderRadius: "4px",
                        minWidth: "240px"
                    }}>
                        <div style={{ color: "white", fontWeight: "bold", marginBottom: "8px", borderBottom: "1px solid rgba(255,255,255,0.2)", paddingBottom: "6px" }}>
                            New City at [{newCityPos[0]}, {newCityPos[1]}]
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <input
                                type="text"
                                placeholder="City name"
                                value={cityForm.name}
                                onChange={(e) => setCityForm(f => ({ ...f, name: e.target.value }))}
                                style={{
                                    padding: "6px 8px",
                                    borderRadius: "4px",
                                    border: "none",
                                    backgroundColor: "#2d3748",
                                    color: "white",
                                    fontSize: "14px"
                                }}
                            />
                            <select
                                value={cityForm.size}
                                onChange={(e) => setCityForm(f => ({ ...f, size: e.target.value }))}
                                style={{
                                    padding: "6px 8px",
                                    borderRadius: "4px",
                                    border: "none",
                                    backgroundColor: "#2d3748",
                                    color: "white",
                                    fontSize: "14px",
                                    cursor: "pointer"
                                }}
                            >
                                <option value="town">Town</option>
                                <option value="city">City</option>
                                <option value="metropolis">Metropolis</option>
                            </select>
                            <label style={{ color: "white", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
                                <input type="checkbox" checked={cityForm.hasTrainStation} onChange={(e) => setCityForm(f => ({ ...f, hasTrainStation: e.target.checked }))} />
                                Has Train Station
                            </label>
                            <label style={{ color: "white", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
                                <input type="checkbox" checked={cityForm.hasPort} onChange={(e) => setCityForm(f => ({ ...f, hasPort: e.target.checked }))} />
                                Has Port
                            </label>
                            <label style={{ color: "white", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
                                <input type="checkbox" checked={cityForm.hasSkyport} onChange={(e) => setCityForm(f => ({ ...f, hasSkyport: e.target.checked }))} />
                                Has Skyport
                            </label>
                            {cityError && (
                                <div style={{ color: "#ff6b6b", fontSize: "12px" }}>{cityError}</div>
                            )}
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    onClick={saveCity}
                                    disabled={citySaving || !cityForm.name.trim()}
                                    style={{
                                        flex: 1,
                                        padding: "6px 12px",
                                        backgroundColor: citySaving || !cityForm.name.trim() ? "#4a5568" : "#2ecc71",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: citySaving || !cityForm.name.trim() ? "default" : "pointer",
                                        fontWeight: "bold"
                                    }}
                                >
                                    {citySaving ? "Saving..." : "Save"}
                                </button>
                                <button
                                    onClick={toggleAddingCity}
                                    style={{
                                        flex: 1,
                                        padding: "6px 12px",
                                        backgroundColor: "#4a5568",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer"
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {editingCity && (
                    <div style={{
                        backgroundColor: "rgba(74, 85, 104, 0.95)",
                        padding: "10px 12px",
                        borderRadius: "4px",
                        minWidth: "240px"
                    }}>
                        <div style={{ color: "white", fontWeight: "bold", marginBottom: "8px", borderBottom: "1px solid rgba(255,255,255,0.2)", paddingBottom: "6px" }}>
                            Edit {editingCity.name}
                            <div style={{ fontWeight: "normal", fontSize: "11px", opacity: 0.7 }}>
                                Position: [{editingCity.position[0]}, {editingCity.position[1]}]
                            </div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <input
                                type="text"
                                placeholder="City name"
                                value={editForm.name}
                                onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                                style={{
                                    padding: "6px 8px",
                                    borderRadius: "4px",
                                    border: "none",
                                    backgroundColor: "#2d3748",
                                    color: "white",
                                    fontSize: "14px"
                                }}
                            />
                            <select
                                value={editForm.size}
                                onChange={(e) => setEditForm(f => ({ ...f, size: e.target.value }))}
                                style={{
                                    padding: "6px 8px",
                                    borderRadius: "4px",
                                    border: "none",
                                    backgroundColor: "#2d3748",
                                    color: "white",
                                    fontSize: "14px",
                                    cursor: "pointer"
                                }}
                            >
                                <option value="town">Town</option>
                                <option value="city">City</option>
                                <option value="metropolis">Metropolis</option>
                            </select>
                            <label style={{ color: "white", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
                                <input type="checkbox" checked={editForm.hasTrainStation} onChange={(e) => setEditForm(f => ({ ...f, hasTrainStation: e.target.checked }))} />
                                Has Train Station
                            </label>
                            <label style={{ color: "white", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
                                <input type="checkbox" checked={editForm.hasPort} onChange={(e) => setEditForm(f => ({ ...f, hasPort: e.target.checked }))} />
                                Has Port
                            </label>
                            <label style={{ color: "white", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
                                <input type="checkbox" checked={editForm.hasSkyport} onChange={(e) => setEditForm(f => ({ ...f, hasSkyport: e.target.checked }))} />
                                Has Skyport
                            </label>
                            {cityError && (
                                <div style={{ color: "#ff6b6b", fontSize: "12px" }}>{cityError}</div>
                            )}
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    onClick={updateCity}
                                    disabled={citySaving || !editForm.name.trim()}
                                    style={{
                                        flex: 1,
                                        padding: "6px 12px",
                                        backgroundColor: citySaving || !editForm.name.trim() ? "#4a5568" : "#2ecc71",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: citySaving || !editForm.name.trim() ? "default" : "pointer",
                                        fontWeight: "bold"
                                    }}
                                >
                                    {citySaving ? "Saving..." : "Save"}
                                </button>
                                <button
                                    onClick={deleteCity}
                                    disabled={citySaving}
                                    style={{
                                        flex: 1,
                                        padding: "6px 12px",
                                        backgroundColor: citySaving ? "#4a5568" : "#e74c3c",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: citySaving ? "default" : "pointer",
                                        fontWeight: "bold"
                                    }}
                                >
                                    Delete
                                </button>
                                <button
                                    onClick={cancelEdit}
                                    style={{
                                        flex: 1,
                                        padding: "6px 12px",
                                        backgroundColor: "#4a5568",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer"
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {(measuring || segments.length > 0) && (
                    <div style={{
                        backgroundColor: "rgba(74, 85, 104, 0.9)",
                        padding: "8px 12px",
                        borderRadius: "4px"
                    }}>
                        <div style={{ color: "white", fontSize: "12px", marginBottom: "6px" }}>
                            Travel Mode {segments.length > 0 && "(for next leg)"}
                        </div>
                        <select
                            value={travelMode.id}
                            onChange={(e) => setTravelMode(TRAVEL_MODES.find(m => m.id === e.target.value))}
                            style={{
                                padding: "6px 8px",
                                borderRadius: "4px",
                                border: "none",
                                backgroundColor: "#2d3748",
                                color: "white",
                                fontSize: "14px",
                                cursor: "pointer",
                                width: "100%"
                            }}
                        >
                            {TRAVEL_MODES.map(mode => (
                                <option key={mode.id} value={mode.id}>
                                    {mode.name} ({mode.speed} mph)
                                </option>
                            ))}
                        </select>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            color: "rgba(255,255,255,0.7)",
                            fontSize: "11px",
                            marginTop: "4px"
                        }}>
                            <div style={{
                                width: "10px",
                                height: "10px",
                                backgroundColor: MODE_COLORS[travelMode.category],
                                borderRadius: "2px"
                            }} />
                            {travelMode.hoursPerDay}h/day travel
                        </div>
                    </div>
                )}
                {segments.length > 1 && (
                    <RouteSummary segments={segments} />
                )}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    backgroundColor: "rgba(74, 85, 104, 0.9)",
                    padding: "8px 12px",
                    borderRadius: "4px"
                }}>
                    <span style={{ color: "white", fontSize: "14px" }}>Political</span>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={politicalOpacity * 100}
                        onChange={(e) => setPoliticalOpacity(e.target.value / 100)}
                        style={{ width: "100px", cursor: "pointer" }}
                    />
                    <span style={{ color: "white", fontSize: "14px", width: "40px" }}>
                        {Math.round(politicalOpacity * 100)}%
                    </span>
                </div>

                <ToggleSwitch
                    label="Continents"
                    enabled={showContinents}
                    onToggle={() => setShowContinents(!showContinents)}
                />
                {showContinents && (
                    <div style={{
                        backgroundColor: "rgba(74, 85, 104, 0.9)",
                        padding: "8px 12px",
                        borderRadius: "4px"
                    }}>
                        {STATIC_CONTINENTS.map(continent => (
                            <div
                                key={continent.id}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "4px 0"
                                }}
                            >
                                <div style={{
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: continent.color,
                                    borderRadius: "2px"
                                }} />
                                <span style={{ color: "white", fontSize: "14px" }}>
                                    {continent.name}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                <ToggleSwitch
                    label="Cities"
                    enabled={showCities}
                    onToggle={() => setShowCities(!showCities)}
                />
                {showCities && (
                    <div style={{
                        backgroundColor: "rgba(74, 85, 104, 0.9)",
                        padding: "8px 12px",
                        borderRadius: "4px"
                    }}>
                        {["metropolis", "city", "town"].map(size => (
                            <div
                                key={size}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "4px 0"
                                }}
                            >
                                <div style={{
                                    width: size === "town" ? "10px" : "14px",
                                    height: size === "town" ? "10px" : "14px",
                                    backgroundColor: CITY_INFO[size].color,
                                    border: size === "metropolis" ? "2px solid #8b4513" : "2px solid #333",
                                    borderRadius: size === "town" ? "50%" : "0",
                                    transform: size !== "town" ? "rotate(45deg)" : "none"
                                }} />
                                <span style={{ color: "white", fontSize: "14px" }}>
                                    {CITY_INFO[size].label}
                                </span>
                                <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px" }}>
                                    {CITY_INFO[size].population}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {notesCity && (
                <CityNotesPanel
                    city={notesCity}
                    onClose={() => setNotesCity(null)}
                    onEdit={(city) => startEditCity(city)}
                />
            )}
            <MapContainer
                crs={L.CRS.Simple}
                style={{ height: "100%", width: "100%", backgroundColor: "#202f36" }}
                minZoom={-4}
                maxZoom={4}
                scrollWheelZoom={true}
            >
                <ImageOverlay
                    url="erynos.jpg"
                    bounds={bounds}
                />
                <ImageOverlay
                    url="erynos_political.jpg"
                    bounds={bounds}
                    opacity={politicalOpacity}
                />
                <InitialView bounds={bounds} />
                <MeasurementTool
                    segments={segments}
                    setSegments={setSegments}
                    measuring={measuring}
                    travelMode={travelMode}
                />
                {showContinents && <ContinentOverlays continents={STATIC_CONTINENTS} />}
                {showCities && <CityMarkers cities={cities} onEditCity={onCityClick} measuring={measuring} />}
                <CoordinatePicker enabled={pickingCoords} onCoordinateClick={setLastCoords} />
                <CityPlacer active={addingCity && !newCityPos} onPlace={setNewCityPos} />
            </MapContainer>
        </div>
    );
}